import { ConditionalRender } from '@/components/Helpers/ComponentRender/ConditionalRender';
import { EventTracker } from '@/utils';
import { ConnectionCard } from '@design-system/Cards/ConnectionCard';
import { StandardContainer } from '@design-system/StandardContainer';
import { useKitType } from '@hooks/useKittype';
import { SkeletonView } from '@screens/HealthConnections/HealthConnectionsContainer';
import { HealthDetailsSectionHeader } from '@screens/HealthPages/HealthDetailsSectionHeader';
import { HealthL4ImproveMyScoreInfo } from '@screens/HealthPages/HealthL4ImproveMyScoreInfo';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';
import DoctorsNoteShimmer from '@screens/LevelTwoDetail/DoctorsNoteShimmer';
import ImproveMyScoreShimmer from '@screens/LevelTwoDetail/ImproveMyScoreShimmer';
import {
  useGetDoctorsNotes,
  useGetImproveScoreSummary,
  useGetL4ConnectionList,
  useGetScoreDetails,
} from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { useNavigate, useParams } from 'react-router-dom';

export const HealthL4DetailsPage = () => {
  const { id: scoreId } = useParams();
  const navigate = useNavigate();
  const kitType = useKitType();
  const isOHIKitTypeUser = kitType === 'OHI';

  const {
    data: l4ScoreDetail,
    isLoading: isL4ScoreDetailAPILoading,
    isError: isL4ScoreDetailAPIError,
  } = useGetScoreDetails('L4', Number(scoreId));

  const {
    data: doctorNotes,
    isLoading: isDoctorsNoteLoading,
    isError: isDoctorsNoteError,
  } = useGetDoctorsNotes('L4', Number(scoreId));

  const {
    data: healthConnectionList,
    isLoading: isHealthConnectionAPILoading,
    isError: isHealthConnectionAPIError,
  } = useGetL4ConnectionList(Number(scoreId));

  const {
    data: improveMyScore,
    isLoading: isImproveMyScoreLoading,
    isError: isImproveMyScoreError,
  } = useGetImproveScoreSummary(Number(scoreId));

  const navigateToConditionsPage = (title: string) => {
    return navigate(`/health/connections/${encodeURIComponent(title)}`);
  };

  const renderDoctorsNotWithBg = () => {
    if (!doctorNotes) return;
    return (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://cdn-st.stage.azure.viome.com/viome-cdn/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
        data-testid="healthL4Details-doctorsNoteSection"
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes} data-testid="healthL4Details-doctorsNotes" />
        </StandardContainer>
      </section>
    );
  };

  const renderImproveMyScore = () => {
    return (
      <section className='mb-6' data-testid="healthL4Details-improveScoreSection">
        {improveMyScore && (
          <HealthL4ImproveMyScoreInfo improveMyScoreInfo={improveMyScore} data-testid="healthL4Details-improveScoreInfo" />
        )}
      </section>
    );
  };

  const renderHealthConnections = (title: string) => {
    if (healthConnectionList && healthConnectionList?.length < 1) {
      return;
    }
    // TODO: make it a separate component
    return (
      <div className="mt-12 mb-24" data-testid="healthL4Details-healthConnections">
        <div className="flex flex-col" data-testid="healthL4Details-healthConnectionsHeader">
          <div className="dark typography-h1 text-primary mb-2 flex" data-testid="healthL4Details-healthConnectionsTitle">
            Health Connections
          </div>
          <div className="flex dark typography-body text-secondary mb-6 lg:w-[35rem]">
            We’ve found a connection between this pathway score and a condition.
            This condition may be one you self-reported or an Associated Health
            Risk identified through our research.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="healthL4Details-healthConnectionsGrid">
          {healthConnectionList?.map((connection, index) => (
            <div key={`${connection.name}-${connection.id}`} data-testid={`healthL4Details-connectionCard-${index}`}>
              <ConnectionCard
                name={connection.name}
                noOfConnections={connection.no_of_connections}
                connectionType={connection.connection_type}
                connectionTypeColor={connection.connection_type_color}
                onClick={() => {
                  EventTracker.trackEvent(
                    'health',
                    `${title.replace(/\s+/g, '')}Details_healthConnections_${connection.name.replace(/\s+/g, '')}_reported`,
                  );
                  navigateToConditionsPage(connection.name);
                }}
                data-testid={`healthL4Details-connectionCard-${connection.name.replace(/\s+/g, '-').toLowerCase()}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-black h-full text-primary w-full dark !max-w-full" data-testid="healthL4Details-page">
      <HealthDetailsSectionHeader
        scoreDetail={l4ScoreDetail}
        isScoreDetailsAPILoading={isL4ScoreDetailAPILoading}
        scoreId={Number(scoreId)}
        isL4ScoreDetailAPIError={isL4ScoreDetailAPIError}
        data-testid="healthL4Details-header"
      />
      <ConditionalRender
        // Doctors note conditional rendering
        conditions={[
          { when: isDoctorsNoteLoading, render: <DoctorsNoteShimmer data-testid="healthL4Details-doctorsNoteShimmer" /> },
          { when: isDoctorsNoteError, render: null },
        ]}
        otherwise={renderDoctorsNotWithBg()}
      />
      <StandardContainer data-testid="healthL4Details-contentContainer">
        <ConditionalRender
          // Improve my score conditional rendering
          conditions={[
            {
              when: isImproveMyScoreLoading,
              render: <ImproveMyScoreShimmer data-testid="healthL4Details-improveScoreShimmer" />,
            },
            { when: isImproveMyScoreError, render: null },
          ]}
          otherwise={renderImproveMyScore()}
        />
        <ConditionalRender
          // Health Connections conditional rendering
          conditions={[
            {
              when: isHealthConnectionAPILoading,
              render: (
                <section className="mb-24" data-testid="healthL4Details-healthConnectionsLoadingSection">
                  <SkeletonView data-testid="healthL4Details-healthConnectionsSkeleton" />
                </section>
              ),
            },
            { when: isHealthConnectionAPIError, render: null },
            { when: isOHIKitTypeUser, render: null },
          ]}
          otherwise={renderHealthConnections(l4ScoreDetail?.title ?? '')}
        />
      </StandardContainer>
    </div>
  );
};
